import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Landmark, ShieldCheck, Loader2, Check, Info, Lock, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { resolveBuyerTheme, TAP_BLUE, TAP_FUCHSIA } from '../components/showroom/buyerTheme';
import { attachTactile, feedback, setOrgTactile } from '../components/showroom/tactile';
import { BuyerChatThread } from '../components/showroom/BuyerChatThread';
import { ActionRow } from '../components/showroom/ActionChips';
import { insConvStore } from '../lib/compareStore';
import { vehiclePublicAPI } from '../lib/api';
import { buyerSessionId, useFormDraft, useSessionContact, mergeKnown } from '../components/showroom/useFormDraft';
import { APR, FREQ, TERMS, DEFAULT_TERM_MONTHS, money, payment, parsePrice, defaultDown, priceText } from '../lib/financeMath';
import useBuyerHeartbeat from '../components/showroom/useBuyerHeartbeat';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const CONSENT_VERSION = 'vehicle-2026-09-v1';

const Pick = ({ active, onClick, children, testid, th }) => (
    <button type="button" onClick={onClick} data-testid={testid}
        className={`h-12 min-w-[76px] flex-1 rounded-xl border text-sm font-semibold transition-colors ${active
            ? 'border-emerald-400 bg-emerald-500/20 text-emerald-100 dark:text-emerald-100'
            : th.pickBtn}`}>
        {children}
    </button>
);

/** Finance & Insurance — full-screen takeover, never a modal. */
export default function FinanceInsurance() {
    const { cardId } = useParams();
    const navigate = useNavigate();
    const rootRef = useRef(null);
    // Part 8 — same delegated tactile feedback as the vehicle page.
    useEffect(() => attachTactile(document.body), []);

    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [freq, setFreq] = useState('biweekly');
    const [term, setTerm] = useState(DEFAULT_TERM_MONTHS);
    const [down, setDown] = useState(0);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [flagged, setFlagged] = useState({});
    // Hub → sub-screen navigation. One long scroll used to hold all four of
    // these at once; each now owns its own screen, and `view` IS the depth.
    const [view, setView] = useState('hub');
    // 2C insurance — its own small state machine: ask for contact details only
    // when nothing was captured anywhere in this session (422 needs_contact).
    const [ins, setIns] = useState({});
    const [insBusy, setInsBusy] = useState(false);
    const [insSent, setInsSent] = useState(null);
    const [insNeedContact, setInsNeedContact] = useState(false);
    // The CONFIDENTIAL broker thread — same engine as Contact Sales
    // (db.request_messages via /vehicle/conversation/:id), kept in its own
    // storage key so it can never be resumed as the sales thread.
    const [insReq, setInsReq] = useState('');
    const [insMsgs, setInsMsgs] = useState([]);
    const [insInput, setInsInput] = useState('');
    // Shared with the trade-in screen, and honoured from `?s=` so a saved link
    // resumes this exact half-filled form (Part 6).
    const sessionId = useMemo(() => buyerSessionId(cardId), [cardId]);
    const { resumed, saveDraft } = useFormDraft(cardId, sessionId, 'finance');
    useBuyerHeartbeat(cardId, sessionId);
    const known = useSessionContact(cardId, sessionId);
    useEffect(() => {
        if (!known) return;
        setForm((f) => mergeKnown(f, known, [['full_name', 'name'], ['email', 'email'], ['phone', 'phone']]));
        setIns((i) => mergeKnown(i, known, [['name', 'name'], ['email', 'email'], ['phone', 'phone']]));
    }, [known]);

    useEffect(() => {
        axios.get(`${API}/api/vehicle/${cardId}`).then((r) => {
            setOrgTactile(r.data?.action_noise);
            setData(r.data);
            const price = parsePrice(r.data?.vehicle?.asking_price);
            setDown(defaultDown(price));
            axios.post(`${API}/api/vehicle/${cardId}/fi-activity`,
                { kind: 'calculator_opened', session_id: sessionId }).catch(() => { });
        }).catch(() => setData({ found: false }));
    }, [cardId, sessionId]);

    // Bring back whatever the buyer typed before they walked away.
    useEffect(() => {
        if (!resumed) return;
        setForm((f) => ({ ...f, ...resumed.form }));
        if (resumed.term) setTerm(resumed.term);
        if (resumed.down !== undefined) setDown(resumed.down);
        if (resumed.freq) setFreq(resumed.freq);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumed]);

    // Persist as they type — debounced, and never allowed to interrupt them.
    useEffect(() => {
        if (!data) return;
        const hasContent = Object.values(form || {}).some((v) => String(v || '').trim());
        if (!hasContent) return;
        saveDraft({ form, term, down, freq },
                  { name: form.full_name, email: form.email, phone: form.phone });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, term, down, freq, data]);

    const th = resolveBuyerTheme(data?.buyer_theme);
    const v = data?.vehicle;
    const price = parsePrice(v?.asking_price);
    const financed = Math.max(price - (Number(down) || 0), 0);
    const per = payment(financed, term, freq);
    const plans = v?.warranty_plans || [];
    const fi = data?.fi || {};

    // "Verified Monthly Payments" — the terms the buyer SETTLED on, not the
    // fact that they opened the calculator. Debounced 2.5s so dragging the
    // down-payment slider records one line, not forty.
    useEffect(() => {
        if (!data || !price || !per) return;
        const id = setTimeout(() => {
            vehiclePublicAPI.activity(cardId, 'payment_estimated', sessionId, {
                amount: Math.round(per * 100) / 100, frequency: freq,
                term_months: term, down_payment: Number(down) || 0,
            }).catch(() => { });
        }, 2500);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId, sessionId, per, freq, term, down, price, !!data]);

    const consentText = t('vehicleExp.consentVehicle',
        'I agree to be contacted by {{dealership}} about this vehicle and any financing or trade-in inquiries related to it.',
        { dealership: data?.dealership || '' });

    // Back has depth: a sub-screen returns to the hub, the hub leaves F&I.
    const back = () => (view === 'hub' ? navigate(`/vehicle/${cardId}`) : setView('hub'));

    const submitPrequal = async () => {
        if (!form.full_name?.trim() || !form.email?.trim() || !form.phone?.trim()
            || !form.employment_status || !form.income_range) {
            toast.error(t('fi.prequal.missing', 'Please fill in every field')); return;
        }
        if (!form.consent) { toast.error(t('fi.prequal.consentRequired', 'Please agree to be contacted to continue')); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/api/vehicle/${cardId}/financing-application`, {
                ...form, vehicle_price: price, down_payment: Number(down) || 0,
                term_months: term, payment_frequency: freq, estimated_payment: Math.round(per * 100) / 100,
                consent: true, consent_text: consentText, consent_version: CONSENT_VERSION,
                session_id: sessionId,
            });
            setSent(true);
            feedback('success');
        } catch (e) {
            feedback('error');
            toast.error(e.response?.data?.detail || t('fi.prequal.error', 'Could not send that — please try again'));
        } finally { setBusy(false); }
    };

    const insuranceConsentText = t('fi.insurance.consent',
        'I agree to be contacted about insurance for this vehicle.');

    const loadInsThread = async (reqId) => {
        try {
            const r = await axios.get(`${API}/api/vehicle/conversation/${reqId}`);
            setInsMsgs(r.data?.messages || []);
        } catch { /* keep what is on screen */ }
    };

    // Resume a broker thread this buyer already has for this vehicle.
    useEffect(() => {
        const known = insConvStore.get(cardId);
        if (known) { setInsReq(known); setInsSent({ resumed: true }); }
    }, [cardId]);

    // Opening the insurance screen is reported as a BARE FACT only (the
    // timeline layer forces its params empty) — the desk learns they asked,
    // never what they are writing to the broker.
    useEffect(() => {
        if (view !== 'insurance') return;
        vehiclePublicAPI.activity(cardId, 'insurance_opened', sessionId).catch(() => {});
    }, [view, cardId, sessionId]);

    // Poll for the broker's replies, but only while the thread is on screen.
    useEffect(() => {
        if (view !== 'insurance' || !insReq) return undefined;
        loadInsThread(insReq);
        const id = setInterval(() => loadInsThread(insReq), 8000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, insReq]);

    const sendInsMessage = async () => {
        const text = insInput.trim();
        if (!text || !insReq) return;
        setInsInput('');
        setInsMsgs((m) => [...m, { sender_role: 'customer', body: text, created_at: new Date().toISOString() }]);
        try { await axios.post(`${API}/api/vehicle/conversation/${insReq}/message`, { body: text }); }
        catch { toast.error(t('fi.insurance.error', 'Could not send that — please try again')); }
    };

    const submitInsurance = async () => {
        if (!ins.consent) { toast.error(t('fi.insurance.consentRequired', 'Please agree to be contacted to continue')); return; }
        if (insNeedContact && !ins.email?.trim() && !ins.phone?.trim()) {
            toast.error(t('fi.insurance.contactRequired', 'Add an email or a mobile number so the broker can reply')); return;
        }
        setInsBusy(true);
        try {
            const r = await axios.post(`${API}/api/vehicle/${cardId}/insurance-quote`, {
                message: ins.message || null,
                // Reuse what the buyer already typed in this session first.
                name: ins.name || form.full_name || null,
                email: ins.email || form.email || null,
                phone: ins.phone || form.phone || null,
                consent: true, consent_text: insuranceConsentText,
                consent_version: CONSENT_VERSION, session_id: sessionId,
            });
            const reqId = r.data?.request_id || '';
            setInsSent(r.data || {});
            if (reqId) {
                setInsReq(reqId);
                insConvStore.set(cardId, reqId);
                // What they typed in the prompt IS the first message in the thread.
                setInsMsgs(ins.message?.trim()
                    ? [{ sender_role: 'customer', body: ins.message.trim(), created_at: new Date().toISOString() }]
                    : []);
                loadInsThread(reqId);
            }
            feedback('success');
        } catch (e) {
            if (e.response?.status === 422) {
                setInsNeedContact(true);
                toast.error(t('fi.insurance.contactRequired', 'Add an email or a mobile number so the broker can reply'));
            } else {
                toast.error(e.response?.data?.detail || t('fi.insurance.error', 'Could not send that — please try again'));
            }
        } finally { setInsBusy(false); }
    };

    const flagPlan = async (plan, idx) => {
        try {
            const r = await axios.post(`${API}/api/vehicle/${cardId}/warranty-interest`, {
                plan_id: plan.id || String(idx),
                plan_label: `${plan.duration || ''} — ${plan.coverage || ''}`.trim(),
                name: form.full_name || null, email: form.email || null, phone: form.phone || null,
                session_id: sessionId,
            });
            setFlagged((s) => ({ ...s, [idx]: true }));
            toast.success(r.data?.needs_contact
                ? t('fi.warranty.flaggedAskContact', "Noted — leave your details below and we'll get back to you")
                : t('fi.warranty.flagged', 'Noted — our team will follow up on this plan'));
        } catch {
            toast.error(t('fi.warranty.error', 'Could not send that — please try again'));
        }
    };

    if (!data) {
        return <div className="min-h-screen bg-neutral-950 flex items-center justify-center" data-testid="fi-loading">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-500" /></div>;
    }
    if (data.found === false) {
        return <div className="min-h-screen bg-neutral-950 text-neutral-300 flex flex-col items-center justify-center gap-4 p-6" data-testid="fi-unavailable">
            <p>{t('vehicleExp.notAvailable', 'This vehicle is no longer available.')}</p>
            <Button onClick={back} data-testid="fi-back-empty">{t('common.back', 'Back')}</Button>
        </div>;
    }

    const SCREENS = {
        payment: t('fi.financing.title', 'Estimate your payment'),
        finance: t('fi.hub.finance', 'Finance Team'),
        warranty: t('fi.warranty.available', 'Warranty Available'),
        insurance: t('fi.hub.insurance', 'Ask Insurance Broker for a Quote'),
    };

    // Each hub door keeps its own colour from the palette already on these
    // screens (blue = finance, emerald = the dealership's own team, CARFAX-family
    // gold = warranty, fuchsia = the broker). The colour is the chip RING, so the
    // four doors read as one family of cards rather than four coloured slabs.
    const HUB = [
        ['payment', SCREENS.payment, Calculator, TAP_BLUE, fi.financing],
        ['finance', SCREENS.finance, Landmark, '#34D399', fi.financing],
        ['warranty', SCREENS.warranty, ShieldCheck, '#E3B23C', fi.warranty && plans.length > 0],
        ['insurance', SCREENS.insurance, Lock, TAP_FUCHSIA, fi.insurance],
    ];

    const saveLater = (
        <button onClick={() => navigate(`/vehicle/${cardId}?save=1`)} data-testid="fi-save-later"
            className={`w-full text-center text-xs font-semibold py-1 ${th.muted} hover:opacity-80`}>
            {t('fi.saveLater', 'Save and finish later')}
        </button>
    );

    return (
        <div ref={rootRef} className={`min-h-screen pb-16 ${th.page}`} data-testid="fi-page" data-buyer-theme={th.name}>
            {/* Back has depth: sub-screen → hub → vehicle page. */}
            <div className={`sticky top-0 z-30 flex items-center gap-3 px-4 py-3 ${th.header}`}>
                <button onClick={back} className={`p-2 -ml-2 rounded-full ${th.navIcon}`}
                    aria-label={t('common.back', 'Back')} data-testid="fi-back">
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <div className="min-w-0">
                    <p className={`text-[11px] uppercase tracking-[0.18em] truncate ${th.dealership}`}>{data.dealership}</p>
                    <p className={`text-sm font-bold truncate ${th.title}`} data-testid="fi-screen-title">
                        {view === 'hub' ? t('fi.button', 'Finance & Insurance') : SCREENS[view]}
                    </p>
                </div>
            </div>

            <div className="px-4 pt-4 space-y-4 max-w-md mx-auto">
                <p className={`text-sm ${th.muted}`} data-testid="fi-vehicle-line">
                    {[v?.year, v?.make, v?.model].filter(Boolean).join(' ')}{v?.asking_price ? ` · ${priceText(v.asking_price)}` : ''}
                </p>

                {/* ── HUB — a dedicated page with nothing else on it, so the
                     four doors are STACKED full-width instead of clustering as
                     a grid in the top half and leaving the rest blank. ─── */}
                {view === 'hub' && (
                    <div className="space-y-2.5 pt-1" data-testid="fi-hub">
                        {HUB.filter(([, , , , on]) => on).map(([key, label, Icon, color]) => (
                            <ActionRow key={key} light={th.name === 'light'} color={color} icon={Icon}
                                label={label} onClick={() => setView(key)} testid={`fi-hub-${key}`} />
                        ))}
                    </div>
                )}

                {/* ── SUB-SCREEN 1 — the calculator, on its own ───── */}
                {view === 'payment' && (
                    <section className={`rounded-2xl p-4 space-y-4 ${th.panelSolid}`} data-testid="fi-financing">
                        <h2 className={`flex items-center gap-2 text-base font-bold ${th.sectionTitle}`}>
                            <Landmark className="w-5 h-5" style={{ color: TAP_BLUE }} />
                            {t('fi.financing.title', 'Estimate your payment')}
                        </h2>
                        <div>
                            <Label className={`text-xs ${th.label}`}>{t('fi.financing.frequency', 'How often do you want to pay?')}</Label>
                            <div className="flex gap-2 mt-1.5">
                                {FREQ.map(([key]) => (
                                    <Pick key={key} th={th} active={freq === key} onClick={() => setFreq(key)} testid={`fi-freq-${key}`}>
                                        {t(`fi.financing.freq.${key}`, key)}
                                    </Pick>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className={`text-xs ${th.label}`}>{t('fi.financing.down', 'Down payment')}: <span className="font-bold">{money(down)}</span></Label>
                            <input type="range" min="0" max={Math.max(price, 1000)} step="250" value={down}
                                onChange={(e) => setDown(Number(e.target.value))}
                                className="w-full mt-2 h-8 accent-emerald-500" data-testid="fi-down-slider" />
                        </div>
                        <div>
                            <Label className={`text-xs ${th.label}`}>{t('fi.financing.term', 'Term')}</Label>
                            <div className="flex gap-2 mt-1.5">
                                {TERMS.map((m) => (
                                    <Pick key={m} th={th} active={term === m} onClick={() => setTerm(m)} testid={`fi-term-${m}`}>{m}</Pick>
                                ))}
                            </div>
                        </div>
                        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-center" data-testid="fi-payment-card">
                            <p className={`text-xs uppercase tracking-wide ${th.label}`}>{t('fi.financing.estimated', 'Estimated payment')}</p>
                            <p className="text-4xl font-black text-emerald-400 mt-1" data-testid="fi-payment-value">{money(per)}</p>
                            <p className={`text-xs mt-1 ${th.muted}`} data-testid="fi-payment-freq">
                                {t(`fi.financing.per.${freq}`, freq)} · {term} {t('fi.financing.months', 'months')} · {money(financed)} {t('fi.financing.financed', 'financed')}
                            </p>
                        </div>
                        <p className={`text-[11px] leading-relaxed ${th.faint}`} data-testid="fi-disclaimer">
                            {t('fi.financing.disclaimer', 'An estimate for planning only, at an example rate of {{apr}}% — not an offer of credit or an approval.', { apr: (APR * 100).toFixed(1) })}
                        </p>
                    </section>
                )}

                {/* ── SUB-SCREEN 2 — the pre-qualification application ──
                    Unchanged Phase 1 lead capture: no credit pull, same consent
                    wording, same endpoint. Only its location moved. */}
                {view === 'finance' && (
                    <section className={`rounded-2xl p-4 space-y-3 ${th.panelSolid}`} data-testid="fi-finance-team">
                        {sent ? (
                            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-center" data-testid="fi-prequal-done">
                                <Check className="w-6 h-6 text-emerald-400 mx-auto" />
                                <p className={`mt-1.5 text-sm font-semibold ${th.body}`}>{t('fi.prequal.received', 'Got it — our finance team will follow up shortly.')}</p>
                            </div>
                        ) : (
                            <div className="space-y-2.5" data-testid="fi-prequal-form">
                                <p className={`text-sm font-bold ${th.sectionTitle}`}>{t('fi.prequal.title', 'Get pre-qualified')}</p>
                                <p className={`text-[11px] ${th.muted}`}>{t('fi.prequal.hint', 'No credit check. Our finance team reviews it and follows up.')}</p>
                                <Input className={`h-12 ${th.input}`} placeholder={t('fi.prequal.name', 'Full name')} value={form.full_name || ''}
                                    onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="fi-prequal-name" />
                                <Input className={`h-12 ${th.input}`} placeholder={t('fi.prequal.email', 'Email')} value={form.email || ''}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="fi-prequal-email" />
                                <Input className={`h-12 ${th.input}`} placeholder={t('fi.prequal.phone', 'Phone')} value={form.phone || ''}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="fi-prequal-phone" />
                                <Label className={`text-xs ${th.label}`}>{t('fi.prequal.employment', 'Employment')}</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    {['employed', 'self_employed', 'retired', 'student', 'other'].map((k) => (
                                        <Pick key={k} th={th} active={form.employment_status === k}
                                            onClick={() => setForm({ ...form, employment_status: k })} testid={`fi-emp-${k}`}>
                                            {t(`fi.prequal.emp.${k}`, k)}
                                        </Pick>
                                    ))}
                                </div>
                                <Label className={`text-xs ${th.label}`}>{t('fi.prequal.income', 'Yearly income')}</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    {['under_30k', '30k_50k', '50k_75k', '75k_100k', '100k_150k', 'over_150k'].map((k) => (
                                        <Pick key={k} th={th} active={form.income_range === k}
                                            onClick={() => setForm({ ...form, income_range: k })} testid={`fi-inc-${k}`}>
                                            {t(`fi.prequal.inc.${k}`, k)}
                                        </Pick>
                                    ))}
                                </div>
                                <label className={`flex items-start gap-2 text-sm cursor-pointer ${th.muted}`}>
                                    <Checkbox checked={!!form.consent} onCheckedChange={(c) => setForm({ ...form, consent: c })}
                                        className="mt-0.5" data-testid="fi-prequal-consent" />
                                    <span data-testid="fi-consent-text">{consentText}</span>
                                </label>
                                <Button onClick={submitPrequal} disabled={busy} data-testid="fi-prequal-submit"
                                    className="w-full h-12 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-bold">
                                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                    {t('fi.prequal.submit', 'Send to the finance team')}
                                </Button>
                            </div>
                        )}
                    </section>
                )}

                {/* ── SUB-SCREEN 3 — the plans on THIS vehicle ───── */}
                {view === 'warranty' && (
                    <section className={`rounded-2xl p-4 space-y-3 ${th.panelSolid}`} data-testid="fi-warranty">
                        <h2 className={`flex items-center gap-2 text-base font-bold ${th.sectionTitle}`}>
                            <ShieldCheck className="w-5 h-5 text-emerald-400" />
                            {t('fi.warranty.title', 'Extended warranty')}
                        </h2>
                        <div className="space-y-2.5" data-testid="fi-warranty-plans">
                            {plans.map((p, idx) => (
                                <div key={idx} className={`rounded-xl p-3.5 ${th.panel}`} data-testid={`fi-plan-${idx}`}>
                                    <p className={`font-bold ${th.body}`}>{p.duration}{p.coverage ? ` — ${p.coverage}` : ''}</p>
                                    {p.notes ? <p className={`text-xs mt-1 ${th.muted}`}>{p.notes}</p> : null}
                                    <Button variant="outline" onClick={() => flagPlan(p, idx)} disabled={!!flagged[idx]}
                                        className={`w-full h-11 mt-2.5 rounded-xl font-semibold ${th.pickBtn}`}
                                        data-testid={`fi-plan-info-${idx}`}>
                                        {flagged[idx]
                                            ? <><Check className="w-4 h-4 mr-1.5" />{t('fi.warranty.noted', 'Noted')}</>
                                            : <><Info className="w-4 h-4 mr-1.5" />{t('fi.warranty.moreInfo', 'More Info')}</>}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* ── SUB-SCREEN 4 — CONFIDENTIAL to the broker.
                    Scoping, routing and consent are untouched. ── */}
                {view === 'insurance' && (
                    <section className={`rounded-2xl p-4 space-y-3 ${th.panelSolid}`} data-testid="fi-insurance">
                        <h2 className={`flex items-center gap-2 text-base font-bold ${th.sectionTitle}`}>
                            <Lock className="w-5 h-5" style={{ color: TAP_FUCHSIA }} />
                            {t('fi.insurance.title', 'Insurance quote')}
                        </h2>
                        {insSent ? (
                            <div className="space-y-2.5" data-testid="fi-insurance-thread">
                                {/* The confidentiality promise stays ON the thread, not
                                    only on the form that opened it. */}
                                <p className={`flex items-start gap-1.5 text-[11px] leading-relaxed ${th.faint}`} data-testid="fi-insurance-private-done">
                                    <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                    {t('fi.insurance.private', 'Private: only the insurance broker can read this conversation. Your salesperson is told you asked for a quote, but never sees what you write.')}
                                </p>
                                {insSent.broker_name ? (
                                    <p className={`text-xs font-semibold ${th.body}`} data-testid="fi-insurance-broker-name">
                                        {t('fi.insurance.sentNamed', '{{broker}} will be in touch about your quote.', { broker: insSent.broker_name })}
                                    </p>
                                ) : null}
                                <div className={`rounded-xl overflow-hidden flex flex-col ${th.panel}`}>
                                    {/* Same component as Contact Sales — one chat engine. */}
                                    <BuyerChatThread
                                        prefix="fi-ins-chat"
                                        messages={insMsgs}
                                        input={insInput}
                                        onInputChange={setInsInput}
                                        onSend={sendInsMessage}
                                        peerLabel={t('fi.insurance.broker', 'Broker')}
                                        empty={t('fi.insurance.chatEmpty', 'Your conversation with the insurance broker will appear here.')}
                                        accent="hover:opacity-90"
                                        bubbleMine="text-white"
                                        peerTag="text-fuchsia-400"
                                        note={{
                                            placeholder: t('vehicleExp.typeMessage', 'Type a message…'),
                                            text: t('fi.insurance.replyNote', 'The broker replies here. Keep this page or its link handy and check back — only they can see what you write.'),
                                        }}
                                        tone={{
                                            wrap: '',
                                            peerBubble: th.name === 'light' ? 'bg-black/[0.06] text-neutral-900' : 'bg-white/10 text-neutral-100',
                                            emptyText: th.muted,
                                            inputRow: th.name === 'light' ? 'border-t border-black/10' : 'border-t border-white/10',
                                            input: th.input,
                                            note: th.faint,
                                        }}
                                        mineStyle={{ backgroundColor: TAP_FUCHSIA }}
                                        sendStyle={{ backgroundColor: TAP_FUCHSIA }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2.5" data-testid="fi-insurance-form">
                                <p className={`text-sm ${th.muted}`}>
                                    {t('fi.insurance.hint', 'We can ask the dealership’s insurance broker to send you a quote for this vehicle.')}
                                </p>
                                <p className={`flex items-start gap-1.5 text-[11px] leading-relaxed ${th.faint}`} data-testid="fi-insurance-private">
                                    <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                    {t('fi.insurance.private', 'Private: only the insurance broker can read this conversation. Your salesperson is told you asked for a quote, but never sees what you write.')}
                                </p>
                                <textarea rows={3} value={ins.message || ''}
                                    onChange={(e) => setIns({ ...ins, message: e.target.value })}
                                    placeholder={t('fi.insurance.message', 'Anything the broker should know? (optional)')}
                                    className={`w-full rounded-xl p-3 text-sm ${th.input}`}
                                    data-testid="fi-insurance-message" />
                                {insNeedContact ? (
                                    <>
                                        <Input className={`h-12 ${th.input}`} placeholder={t('fi.insurance.name', 'Your name')}
                                            value={ins.name || ''} onChange={(e) => setIns({ ...ins, name: e.target.value })}
                                            data-testid="fi-insurance-name" />
                                        <Input className={`h-12 ${th.input}`} placeholder={t('fi.insurance.email', 'Email')}
                                            value={ins.email || ''} onChange={(e) => setIns({ ...ins, email: e.target.value })}
                                            data-testid="fi-insurance-email" />
                                        <Input className={`h-12 ${th.input}`} placeholder={t('fi.insurance.phone', 'Mobile number')}
                                            value={ins.phone || ''} onChange={(e) => setIns({ ...ins, phone: e.target.value })}
                                            data-testid="fi-insurance-phone" />
                                    </>
                                ) : null}
                                <label className={`flex items-start gap-2 text-sm cursor-pointer ${th.muted}`}>
                                    <Checkbox checked={!!ins.consent} onCheckedChange={(c) => setIns({ ...ins, consent: c })}
                                        className="mt-0.5" data-testid="fi-insurance-consent" />
                                    <span data-testid="fi-insurance-consent-text">{insuranceConsentText}</span>
                                </label>
                                <Button onClick={submitInsurance} disabled={insBusy} data-testid="fi-insurance-submit"
                                    className="w-full h-12 rounded-2xl font-bold text-white hover:opacity-90"
                                    style={{ backgroundColor: TAP_FUCHSIA }}>
                                    {insBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                    {t('fi.insurance.submit', 'Ask the broker for a quote')}
                                </Button>
                            </div>
                        )}
                    </section>
                )}

                {/* Part 6 — offered where a half-filled form actually exists. */}
                {(view === 'finance' || view === 'insurance') ? saveLater : null}
            </div>
        </div>
    );
}
